import { useState } from 'react';
import { useToast } from '../../lib/use-toast';
import { attachTruckDocument, getTruckDocumentDownloadUrl, removeTruckDocument, updateTruckDocument, uploadTruckDocumentFile } from './trucks.api';

export interface DocumentFormInput {
  documentTypeId: string;
  expiryDate: string;
  /** Present to upload a new file (required when attaching; optional when editing — omitted keeps the current file). */
  file?: File;
}

/** The Documents tab's mutations: presign → direct upload → confirm (CLAUDE.md → storage rule) — the file never passes through the API body. */
export function useTruckDocuments(truckId: string, reload: () => Promise<void>) {
  const [documentSubmitting, setDocumentSubmitting] = useState(false);
  const toast = useToast();

  async function addDocument(input: DocumentFormInput & { file: File }): Promise<boolean> {
    setDocumentSubmitting(true);
    try {
      const { key, contentType } = await uploadTruckDocumentFile(truckId, input.file);
      await attachTruckDocument(truckId, { key, contentType, documentTypeId: input.documentTypeId, expiryDate: input.expiryDate });
      await reload();
      toast.success('Document added');
      return true;
    } catch {
      toast.error("Couldn't add the document");
      return false;
    } finally {
      setDocumentSubmitting(false);
    }
  }

  async function editDocument(documentId: string, input: DocumentFormInput): Promise<boolean> {
    setDocumentSubmitting(true);
    try {
      const uploaded = input.file ? await uploadTruckDocumentFile(truckId, input.file) : undefined;
      await updateTruckDocument(truckId, documentId, {
        key: uploaded?.key,
        contentType: uploaded?.contentType,
        documentTypeId: input.documentTypeId,
        expiryDate: input.expiryDate,
      });
      await reload();
      toast.success('Document updated');
      return true;
    } catch {
      toast.error("Couldn't update the document");
      return false;
    } finally {
      setDocumentSubmitting(false);
    }
  }

  async function removeDocument(documentId: string): Promise<void> {
    try {
      await removeTruckDocument(truckId, documentId);
      await reload();
      toast.success('Document removed');
    } catch {
      toast.error("Couldn't remove the document");
    }
  }

  async function openDocument(documentId: string): Promise<void> {
    try {
      const { url } = await getTruckDocumentDownloadUrl(truckId, documentId);
      window.open(url, '_blank', 'noopener');
    } catch {
      toast.error("Couldn't open the document");
    }
  }

  return { documentSubmitting, addDocument, editDocument, removeDocument, openDocument };
}
